/* Connection presentation rules. Pure.
 *
 * The one judgement worth stating here: `registered` and `failed` are different facts and must never
 * share a tone. "Nobody has tried to reach this yet" and "we tried and it refused us" send an
 * operator to entirely different places, and a screen that renders both as amber makes them guess.
 */

import type { BadgeTone } from '@/shared/ui/StatusBadge';
import type { Connection } from '@/shared/api/generated/catalog';

export function connectionTone(connection: Connection): BadgeTone {
  switch (connection.state) {
    case 'verified':
      return 'ok';
    case 'failed':
      return 'danger';
    case 'disabled':
      return 'neutral';
    default:
      /* `registered`. `info`, not `warn`: a source registered during onboarding and not yet verified
       * is the expected state, not a problem someone has to resolve today. */
      return 'info';
  }
}

/** Whether a harvest of this source can track renames.
 *
 * `false` has a real cost and it is surfaced before the first harvest rather than after: without a
 * stable object id from the source, a renamed table is indistinguishable from a drop plus a create,
 * so its schema history stays with the old identity. An operator registering MySQL should know that
 * at registration time, not when a rename silently splits a dataset in two.
 */
export function tracksRenames(connection: Connection): boolean {
  return connection.capability.supplies_object_id;
}

/** A connection can be harvested once it has been verified.
 *
 * Harvesting an unverified source is not forbidden by the backend, but offering it as the primary
 * action invites an operator to queue a harvest against credentials nobody has tested — which fails
 * minutes later, asynchronously, where the failure is much harder to connect to the cause.
 */
export function readyToHarvest(connection: Connection): boolean {
  return connection.state === 'verified';
}

/** Display names, because `postgresql` is not what a DBA calls it.
 *
 * On the entity rather than in a feature: two features render a source's family now — the
 * connections screens and the catalogue's source cards — and a second copy is how one screen comes
 * to say `MySQL` while the other says `mysql`.
 */
export const CONNECTOR_LABEL: Readonly<Record<Connection['connector_type'], string>> = {
  postgresql: 'PostgreSQL',
  oracle: 'Oracle',
  mysql: 'MySQL',
  sqlserver: 'SQL Server',
  snowflake: 'Snowflake',
  databricks: 'Databricks',
  bigquery: 'BigQuery',
};

/** `host:port/database`, the way a DBA would write it.
 *
 * Never the `secret_ref`. The path is echoed by the API because it is a path and not a secret, but
 * it names where a customer's credentials live and belongs on the connection's own screen rather
 * than on every card that mentions the source.
 */
export function addressOf(connection: Connection): string {
  return `${connection.host}:${String(connection.port)}/${connection.database}`;
}
